/* eslint-disable @typescript-eslint/no-empty-object-type */
import {Accordion, HStack} from "@chakra-ui/react";
import * as React from "react";
import {LuChevronDown} from "react-icons/lu";
import useTranslate from "platformed-components/i18n/useTranslate";

interface AccordionItemTriggerProps extends Accordion.ItemTriggerProps {
  indicatorPlacement?: "start" | "end";
}

export const AccordionItemTrigger = React.forwardRef<HTMLButtonElement, AccordionItemTriggerProps>(
  function AccordionItemTrigger(props, ref) {
    const {children, indicatorPlacement = "end", ...rest} = props;
    return (
      <Accordion.ItemTrigger {...rest} ref={ref}>
        {indicatorPlacement === "start" && (
          <Accordion.ItemIndicator rotate={{base: "-90deg", _open: "0deg"}}>
            <LuChevronDown />
          </Accordion.ItemIndicator>
        )}
        <HStack gap="4" flex="1" textAlign="start" width="full">
          {children}
        </HStack>
        {indicatorPlacement === "end" && (
          <Accordion.ItemIndicator>
            <LuChevronDown />
          </Accordion.ItemIndicator>
        )}
      </Accordion.ItemTrigger>
    );
  },
);

/** A trigger reduced to its indicator, for item headers that themselves contain
 * links or buttons: interactive content cannot nest inside the full-width
 * trigger's `<button>`, so the indicator alone toggles the item. The padding is
 * hit area, not spacing — the chevron on its own is a small target. */
export const AccordionItemIndicatorTrigger = React.forwardRef<HTMLButtonElement, Accordion.ItemTriggerProps>(
  function AccordionItemIndicatorTrigger(props, ref) {
    const t = useTranslate();
    return (
      <Accordion.ItemContext>
        {({expanded}) => (
          <Accordion.ItemTrigger
            aria-label={expanded ? t("c.Collapse") : t("c.Expand")}
            w="auto"
            flex="0 0 auto"
            p={1}
            cursor="pointer"
            color="fg.muted"
            {...props}
            ref={ref}
          >
            <Accordion.ItemIndicator rotate={{base: "-90deg", _open: "0deg"}}>
              <LuChevronDown />
            </Accordion.ItemIndicator>
          </Accordion.ItemTrigger>
        )}
      </Accordion.ItemContext>
    );
  },
);

interface AccordionItemContentProps extends Accordion.ItemContentProps {}

export const AccordionItemContent = React.forwardRef<HTMLDivElement, AccordionItemContentProps>(
  function AccordionItemContent(props, ref) {
    return (
      <Accordion.ItemContent>
        <Accordion.ItemBody {...props} ref={ref} />
      </Accordion.ItemContent>
    );
  },
);

export const AccordionRoot = Accordion.Root;
export const AccordionItem = Accordion.Item;
